import React, { useEffect, useState } from "react";
import { Button } from "../atoms";
import { FormField } from "../molecules";
import { AdminLayout } from "../organisms";
import { contentService } from "../../services/contentService";
import { settingsService } from "../../services/settingsService";
import { resolveImageUrl } from "../../utils/imageUrl";

// Field schemas — drives the form rendering per section.
const SCHEMAS = {
  hero: [
    { name: "eyebrow", label: "Eyebrow" },
    { name: "headlinePre", label: "Headline (before accent)" },
    { name: "headlineAccent", label: "Headline accent (highlighted word)" },
    { name: "headlinePost", label: "Headline (after accent)" },
    { name: "subCopy", label: "Sub-copy", textarea: true },
    { name: "primaryCta.label", label: "Primary CTA label" },
    {
      name: "primaryCta.target",
      label: "Primary CTA scroll target (section id)",
    },
    { name: "secondaryCta.label", label: "Secondary CTA label" },
    {
      name: "secondaryCta.target",
      label: "Secondary CTA scroll target (section id)",
    },
  ],
  mission: [
    { name: "eyebrow", label: "Eyebrow" },
    { name: "heading", label: "Heading" },
    { name: "body", label: "Body", textarea: true },
  ],
  stories: [
    { name: "eyebrow", label: "Eyebrow" },
    { name: "heading", label: "Heading" },
  ],
  donate: [
    { name: "heading", label: "Heading" },
    { name: "body", label: "Body", textarea: true },
  ],
  contact: [
    { name: "heading", label: "Heading" },
    { name: "body", label: "Body", textarea: true },
  ],
  footer: [
    { name: "brand", label: "Brand line" },
    { name: "copyright", label: "Copyright" },
    { name: "scripture", label: "Scripture line" },
  ],
};

const SECTIONS = ["hero", "mission", "stories", "donate", "contact", "footer"];

const get = (obj, path) =>
  path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
const set = (obj, path, value) => {
  const keys = path.split(".");
  const next = { ...obj };
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return next;
};

export const ContentSettings = () => {
  const [content, setContent] = useState(null);

  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [bannerBySection, setBannerBySection] = useState({});
  const [uploadingHero, setUploadingHero] = useState(false);

  const [donationEnabled, setDonationEnabled] = useState(true);
  const [togglingDonation, setTogglingDonation] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("/");
  const [pendingSave, setPendingSave] = useState(null);
  const [savingFromPreview, setSavingFromPreview] = useState(false);

  useEffect(() => {
    Promise.all([
      contentService.getContent(),
      settingsService.getDonationEnabled(),
    ])
      .then(([contentData, donationVisible]) => {
        setContent(contentData);
        setDonationEnabled(donationVisible !== false);
      })
      .catch(() => {
        setBannerBySection({
          hero: { type: "error", text: "Failed to load content" },
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleField = (section, path, value) =>
    setContent((c) => ({ ...c, [section]: set(c[section], path, value) }));

  const handleCategoryField = (catIndex, field, value) =>
    setContent((c) => {
      const cats = [...(c.contact.helpCategories || [])];
      cats[catIndex] = { ...cats[catIndex], [field]: value };
      return { ...c, contact: { ...c.contact, helpCategories: cats } };
    });

  const handleCategoryItems = (catIndex, text) => {
    const items = text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    handleCategoryField(catIndex, "items", items);
  };

  const getPreviewAnchor = (action) => {
    const section = action?.section;
    const map = {
      hero: "home",
      mission: "about",
      stories: "stories",
      donate: "donate",
      contact: "contact",
      footer: "footer",
    };
    return map[section] ? `#${map[section]}` : "";
  };

  const openPreview = (action = null) => {
    const anchor = getPreviewAnchor(action);
    try {
      sessionStorage.setItem(
        "landingPreviewDraft",
        JSON.stringify({ content, donationEnabled }),
      );
    } catch (_) {
      // Ignore preview draft persistence issues and continue with normal preview.
    }
    setPreviewUrl(`/?preview=1&t=${Date.now()}${anchor}`);
    setPreviewOpen(true);
  };

  const openPreviewBeforeSave = (action) => {
    setPendingSave(action);
    openPreview(action);
  };

  const handleSave = async (section) => {
    setSavingSection(section);
    setBannerBySection((b) => ({ ...b, [section]: null }));
    try {
      const saved = await contentService.updateSection(
        section,
        content[section],
      );
      setContent((c) => ({ ...c, [section]: saved }));
      setBannerBySection((b) => ({
        ...b,
        [section]: { type: "ok", text: "Saved." },
      }));
      setTimeout(
        () => setBannerBySection((b) => ({ ...b, [section]: null })),
        3000,
      );
      return true;
    } catch (err) {
      setBannerBySection((b) => ({
        ...b,
        [section]: { type: "error", text: err.message || "Failed to save" },
      }));
      return false;
    } finally {
      setSavingSection(null);
    }
  };

  const handleHeroImage = async (file) => {
    setUploadingHero(true);
    setBannerBySection((b) => ({ ...b, hero: null }));
    try {
      const { imageUrl } = await contentService.uploadImage(file);
      handleField("hero", "backgroundImage", imageUrl);
    } catch (err) {
      setBannerBySection((b) => ({
        ...b,
        hero: { type: "error", text: err.message || "Failed to upload image" },
      }));
    } finally {
      setUploadingHero(false);
    }
  };

  const executePendingSave = async () => {
    if (!pendingSave) return;
    setSavingFromPreview(true);
    let saved = false;

    if (pendingSave.type === "section") {
      saved = await handleSave(pendingSave.section);
    }

    setSavingFromPreview(false);
    if (saved) {
      setPreviewOpen(false);
      setPendingSave(null);
    }
  };

  const getPreviewChangeLabel = () => {
    if (!pendingSave) return "New content";
    if (pendingSave.type === "section") {
      const sectionName = pendingSave.section
        ? `${pendingSave.section.charAt(0).toUpperCase()}${pendingSave.section.slice(1)}`
        : "Section";
      return `New content: ${sectionName}`;
    }
    return "New content";
  };

  const handleToggleDonation = async () => {
    const next = !donationEnabled;
    setTogglingDonation(true);
    setBannerBySection((b) => ({ ...b, donate: null }));
    try {
      const { enabled } = await settingsService.updateDonationEnabled(next);
      setDonationEnabled(enabled);
      setBannerBySection((b) => ({
        ...b,
        donate: {
          type: "ok",
          text: `Donation section ${enabled ? "shown on" : "hidden from"} the landing page.`,
        },
      }));
      setTimeout(
        () => setBannerBySection((b) => ({ ...b, donate: null })),
        4000,
      );
    } catch (err) {
      setBannerBySection((b) => ({
        ...b,
        donate: {
          type: "error",
          text: err.message || "Failed to update donation visibility",
        },
      }));
    } finally {
      setTogglingDonation(false);
    }
  };

  if (loading || !content) {
    return (
      <AdminLayout title="Content">
        <p className="text-ink-soft">Loading…</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Content">
      <div className="max-w-3xl mx-auto">
        <p className="text-ink-soft mb-6 text-center">
          Edit copy that appears on the landing page. Save per section.
        </p>

        <div className="space-y-6">
          {SECTIONS.map((section) => (
            <section
              key={section}
              className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 p-6"
            >
              <h2 className="font-display text-xl text-terracotta-800 capitalize mb-4">
                {section}
              </h2>

              {bannerBySection[section] && (
                <div
                  className={`mb-4 px-4 py-3 rounded-lg border ${
                    bannerBySection[section].type === "ok"
                      ? "bg-green-100 border-green-400 text-green-700"
                      : "bg-red-100 border-red-400 text-red-700"
                  }`}
                >
                  {bannerBySection[section].text}
                </div>
              )}

              <div className="space-y-4">
                {SCHEMAS[section].map((field) =>
                  field.textarea ? (
                    <div key={field.name}>
                      <label className="block text-sm font-semibold mb-2 text-ink">
                        {field.label}
                      </label>
                      <textarea
                        rows={3}
                        value={get(content[section], field.name) ?? ""}
                        onChange={(e) =>
                          handleField(section, field.name, e.target.value)
                        }
                        className="w-full px-5 py-3 bg-white border border-terracotta-200 rounded-xl text-ink focus:outline-none focus:border-terracotta-400 focus:ring-2 focus:ring-terracotta-100"
                      />
                    </div>
                  ) : (
                    <FormField
                      key={field.name}
                      label={field.label}
                      value={get(content[section], field.name) ?? ""}
                      onChange={(e) =>
                        handleField(section, field.name, e.target.value)
                      }
                    />
                  ),
                )}

                {section === "hero" && (
                  <div className="pt-2 border-t border-terracotta-100/60">
                    <p className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-2">
                      Hero background image (optional)
                    </p>
                    {content.hero.backgroundImage ? (
                      <div className="flex items-center gap-4">
                        <img
                          src={resolveImageUrl(content.hero.backgroundImage)}
                          alt="Hero background preview"
                          className="h-20 w-32 object-cover rounded-lg border border-terracotta-100"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleField("hero", "backgroundImage", "")
                          }
                          className="text-sm text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-full"
                        >
                          Remove image
                        </button>
                      </div>
                    ) : (
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-cream border border-terracotta-200 rounded-full cursor-pointer text-sm">
                        <span>
                          {uploadingHero ? "Uploading…" : "Upload image"}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={uploadingHero}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleHeroImage(f);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    )}
                  </div>
                )}

                {section === "donate" && (
                  <div className="pt-4 mt-2 border-t border-terracotta-100/60">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-display text-lg text-ink">
                          Donation Section Visibility
                        </h3>
                        <p className="text-sm text-ink-soft mt-1">
                          Show or hide the donation section on the landing page.
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={donationEnabled}
                        aria-label="Toggle donation section"
                        disabled={togglingDonation}
                        onClick={handleToggleDonation}
                        className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                          donationEnabled ? "bg-ink-teal" : "bg-ink-soft/30"
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                            donationEnabled ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-sm mt-3 font-medium text-ink-soft">
                      Currently:{" "}
                      <span
                        className={
                          donationEnabled ? "text-green-600" : "text-red-600"
                        }
                      >
                        {donationEnabled ? "Shown" : "Hidden"}
                      </span>
                    </p>
                  </div>
                )}

                {section === "contact" && (
                  <div className="pt-4 mt-2 border-t border-terracotta-100/60 space-y-6">
                    {/* Help Categories Editor */}
                    <div>
                      <h3 className="font-display text-lg text-ink mb-1">
                        Help Categories
                      </h3>
                      <p className="text-sm text-ink-soft mb-5">
                        These 3 tabs appear on the contact form. Edit the label
                        and the list of help items (one per line).
                      </p>
                      <div className="space-y-5">
                        {(content.contact.helpCategories || []).map(
                          (cat, i) => (
                            <div
                              key={cat.id || i}
                              className="bg-cream/60 rounded-xl p-4 border border-terracotta-100"
                            >
                              <div className="mb-3">
                                <FormField
                                  label="Label"
                                  value={cat.label || ""}
                                  onChange={(e) =>
                                    handleCategoryField(
                                      i,
                                      "label",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-semibold mb-2 text-ink">
                                  Items{" "}
                                  <span className="text-ink-soft font-normal">
                                    (one per line)
                                  </span>
                                </label>
                                <textarea
                                  rows={6}
                                  value={(cat.items || []).join("\n")}
                                  onChange={(e) =>
                                    handleCategoryItems(i, e.target.value)
                                  }
                                  className="w-full px-4 py-3 bg-white border border-terracotta-200 rounded-xl text-ink text-sm focus:outline-none focus:border-terracotta-400 focus:ring-2 focus:ring-terracotta-100 resize-none"
                                />
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  variant="primary"
                  className="!px-3 !py-1.5 !text-sm"
                  onClick={() =>
                    openPreviewBeforeSave({ type: "section", section })
                  }
                  disabled={savingSection === section}
                >
                  Preview and Save
                </Button>
              </div>
            </section>
          ))}
        </div>
      </div>

      {previewOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewOpen(false);
          }}
        >
          <div className="w-full max-w-6xl h-[88vh] bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-terracotta-100/60 flex items-center justify-between bg-cream/60">
              <h3 className="font-display text-lg text-ink">
                Landing Page Preview
              </h3>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="text-ink-soft/70 hover:text-ink text-xl leading-none"
                aria-label="Close preview"
              >
                ✕
              </button>
            </div>
            <div className="relative w-full flex-1">
              <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-10 bg-red-400 border border-red-500 text-green-800 rounded-lg px-3 py-2 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide">
                  Preview Mode!
                </p>
                <p className="text-sm font-medium">{getPreviewChangeLabel()}</p>
              </div>
              <iframe
                title="Landing page preview"
                src={previewUrl}
                className="w-full h-full border-0"
              />
            </div>
            <div className="px-5 py-3 border-t border-terracotta-100/60 bg-cream/30 flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                className="!px-3 !py-1.5 !text-sm"
                onClick={() => setPreviewOpen(false)}
              >
                Close
              </Button>
              <Button
                variant="primary"
                className="!px-3 !py-1.5 !text-sm"
                onClick={executePendingSave}
                disabled={savingFromPreview || savingSection !== null}
              >
                {savingFromPreview ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
